import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import * as ExcelJS from 'exceljs';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { LoadingOverlayComponent } from '../../shared/components/loading-overlay/loading-overlay.component';
import { getCurrentUserId } from '../../shared/utils/auth-user.util';
import {
  AtypicalPriceReportResponse,
  AtypicalPriceReportService
} from '../../service/atypical-price-report.service';

type ColumnSpec = { key: string; label: string; minWidth?: number };

const PREVIEW_COLUMNS: ColumnSpec[] = [
  { key: 'variedad_codigo_INE', label: 'Código INE variedad', minWidth: 175 },
  { key: 'variedad_nombre', label: 'Variedad', minWidth: 260 },
  { key: 'variedad_umedida', label: 'Unidad variedad', minWidth: 155 },
  { key: 'variedad_cantidad_base', label: 'Cantidad base variedad', minWidth: 185 },
  { key: 'precio_base_promedio', label: 'Precio base promedio', minWidth: 175 },
  { key: 'precio_minimo', label: 'Precio mínimo', minWidth: 145 },
  { key: 'precio_maximo', label: 'Precio máximo', minWidth: 145 },
  { key: 'numero_cotizaciones', label: 'Cotizaciones', minWidth: 130 },
  { key: 'variedad_pk', label: 'PK variedad', minWidth: 115 },
  { key: 'articulo_pk', label: 'PK artículo', minWidth: 115 },
  { key: 'articulo_codigo_INE', label: 'Código INE artículo', minWidth: 180 },
  { key: 'articulo_nombre', label: 'Artículo', minWidth: 260 },
  { key: 'precio_base_actual', label: 'Precio base actual', minWidth: 165 },
  { key: 'detalle_unidad_medida', label: 'Unidad de medida', minWidth: 170 },
  { key: 'cantidad_actual', label: 'Cantidad actual', minWidth: 145 },
  { key: 'factor_conversion', label: 'Factor conversión', minWidth: 150 },
  { key: 'cantidad_base', label: 'Cantidad base', minWidth: 145 },
  { key: 'anio', label: 'Año', minWidth: 95 },
  { key: 'mes', label: 'Mes', minWidth: 90 },
  { key: 'boleta_codigo_INE', label: 'Código INE boleta', minWidth: 175 },
  { key: 'fuente_nombre', label: 'Fuente', minWidth: 260 }
];

const INTEGER_FIELDS = new Set([
  'anio', 'mes', 'numero_cotizaciones', 'variedad_pk', 'articulo_pk',
  'boleta_pk', 'boleta_detalle_pk', 'fuente_pk', 'boleta_detalle_fk_umedida'
]);

const COLUMN_LABELS: Record<string, string> = {
  ...Object.fromEntries(PREVIEW_COLUMNS.map((column) => [column.key.toLowerCase(), column.label])),
  boleta_pk: 'PK boleta',
  boleta_detalle_pk: 'PK detalle de boleta',
  fuente_pk: 'PK fuente',
  boleta_detalle_fk_umedida: 'PK unidad de medida del detalle'
};

@Component({
  selector: 'app-atypical-prices-report',
  standalone: true,
  imports: [CommonModule, FormsModule, AgGridAngular, LoadingOverlayComponent],
  templateUrl: './atypical-prices-report.html',
  styleUrl: './atypical-prices-report.scss'
})
export class AtypicalPricesReportComponent {
  private readonly reportService = inject(AtypicalPriceReportService);

  fechaInicial = AtypicalPricesReportComponent.firstDayOfCurrentMonth();
  fechaFinal = AtypicalPricesReportComponent.lastDayOfCurrentMonth();
  porcentajeVariacion = 20;

  readonly rows = signal<Array<Record<string, unknown>>>([]);
  readonly returnedColumns = signal<string[]>([]);
  readonly loading = signal(false);
  readonly loadingMessage = signal('Consultando precios atípicos...');
  readonly message = signal('');
  readonly previewOpen = signal(false);
  readonly hasRows = computed(() => this.rows().length > 0);
  readonly previewColumnDefs = computed<ColDef<Record<string, unknown>>[]>(() => {
    const columns: ColDef<Record<string, unknown>>[] = [];

    PREVIEW_COLUMNS.forEach((column) => {
      const field = this.resolveReturnedColumn(column.key);
      if (!field) {
        return;
      }

      columns.push({
        field,
        headerName: column.label,
        minWidth: column.minWidth ?? 140,
        sortable: true,
        resizable: true,
        filter: true,
        valueFormatter: (params: ValueFormatterParams<Record<string, unknown>, unknown>) =>
          this.formatValue(params.value, params.colDef.field)
      });
    });

    return columns;
  });
  readonly previewRangeLabel = computed(() =>
    `${AtypicalPricesReportComponent.displayDate(this.fechaInicial)} al ${AtypicalPricesReportComponent.displayDate(this.fechaFinal)}`
  );
  readonly defaultColumnDef: ColDef<Record<string, unknown>> = {
    sortable: true,
    resizable: true,
    filter: true
  };

  async preview(): Promise<void> {
    const response = await this.fetchReport('Consultando precios atípicos para la previsualización...');
    if (!response) {
      return;
    }

    this.previewOpen.set(response.payload.length > 0);
    this.message.set(response.payload.length
      ? `Vista previa: ${response.payload.length} registro(s) encontrado(s).`
      : 'No se encontraron precios atípicos para los filtros indicados.');
  }

  closePreview(): void {
    this.previewOpen.set(false);
  }

  async exportExcel(): Promise<void> {
    const response = await this.fetchReport('Generando el archivo Excel...');
    if (!response) {
      return;
    }

    if (!response.payload.length) {
      this.message.set('No hay información para exportar.');
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      this.addWorksheet(workbook, 'Previsualización', this.previewColumnsForExport(), response.payload);
      this.addWorksheet(workbook, 'Datos completos', response.columns, response.payload);

      const content = await workbook.xlsx.writeBuffer();
      const link = document.createElement('a');
      const url = URL.createObjectURL(new Blob([content], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }));

      link.href = url;
      link.download = `reporte_precios_atipicos_${this.fechaInicial}_a_${this.fechaFinal}.xlsx`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
      this.message.set('Reporte generado correctamente.');
    } catch (error) {
      console.error('Error generando el reporte de precios atípicos', error);
      this.message.set('No fue posible generar el archivo Excel.');
    }
  }

  private async fetchReport(message: string): Promise<AtypicalPriceReportResponse | null> {
    if (!this.validPeriod()) {
      return null;
    }

    this.loading.set(true);
    this.loadingMessage.set(message);
    this.message.set('');

    try {
      const response = await firstValueFrom(this.reportService.getReport({
        fechaInicial: this.fechaInicial,
        fechaFinal: this.fechaFinal,
        porcentajeVariacion: Number(this.porcentajeVariacion),
        segUsuario: getCurrentUserId()
      }));

      const normalizedResponse: AtypicalPriceReportResponse = {
        columns: response.columns ?? [],
        payload: response.payload ?? [],
        total: Number(response.total ?? response.payload?.length ?? 0)
      };
      this.returnedColumns.set(normalizedResponse.columns);
      this.rows.set(normalizedResponse.payload);
      return normalizedResponse;
    } catch (error) {
      console.error('Error consultando el reporte de precios atípicos', error);
      this.message.set('No fue posible obtener el reporte de precios atípicos.');
      return null;
    } finally {
      this.loading.set(false);
    }
  }

  private validPeriod(): boolean {
    const start = new Date(`${this.fechaInicial}T00:00:00`);
    const end = new Date(`${this.fechaFinal}T00:00:00`);
    const variation = Number(this.porcentajeVariacion);

    if (!this.fechaInicial || !this.fechaFinal ||
      Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) {
      this.message.set('El rango de fechas no es válido.');
      return false;
    }

    if (!Number.isInteger(variation) || variation < 0) {
      this.message.set('El porcentaje de variación debe ser un número entero igual o mayor que cero.');
      return false;
    }

    return true;
  }

  private addWorksheet(
    workbook: ExcelJS.Workbook,
    name: string,
    columns: string[],
    rows: Array<Record<string, unknown>>
  ): void {
    const worksheet = workbook.addWorksheet(name);
    const selectedColumns = columns.filter((column) => this.resolveReturnedColumn(column));
    const actualColumns = selectedColumns.map((column) => this.resolveReturnedColumn(column) ?? column);

    worksheet.columns = actualColumns.map((column) => ({
      header: this.columnLabel(column),
      key: column,
      width: Math.max(16, Math.min(36, this.columnLabel(column).length + 5))
    }));
    worksheet.addRows(rows.map((row) =>
      Object.fromEntries(actualColumns.map((column) => [column, this.excelValue(row[column])]))
    ));

    worksheet.views = [{ state: 'frozen', ySplit: 1 }];
    const lastColumn = worksheet.getRow(1).cellCount;
    worksheet.autoFilter = { from: 'A1', to: `${this.columnLetter(lastColumn)}1` };
    worksheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
    worksheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F766E' } };
  }

  private previewColumnsForExport(): string[] {
    return PREVIEW_COLUMNS
      .map((column) => this.resolveReturnedColumn(column.key))
      .filter((column): column is string => !!column);
  }

  private resolveReturnedColumn(column: string): string | null {
    return this.returnedColumns().find((item) => item.toLowerCase() === column.toLowerCase()) ?? null;
  }

  private columnLabel(column: string): string {
    return COLUMN_LABELS[column.toLowerCase()] ?? column.replaceAll('_', ' ');
  }

  private formatValue(value: unknown, field?: string | null): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    const numericValue = typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))
        ? Number(value)
        : null;

    if (numericValue !== null && Number.isFinite(numericValue)) {
      if (field && INTEGER_FIELDS.has(field.toLowerCase())) {
        return new Intl.NumberFormat('es-GT', {
          useGrouping: false,
          maximumFractionDigits: 0
        }).format(Math.trunc(numericValue));
      }

      return new Intl.NumberFormat('es-GT', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 6
      }).format(numericValue);
    }

    return `${value}`;
  }

  private excelValue(value: unknown): unknown {
    if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
      return Number(value);
    }

    return value ?? '';
  }

  private static toInputDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private static firstDayOfCurrentMonth(): string {
    const now = new Date();
    return AtypicalPricesReportComponent.toInputDate(new Date(now.getFullYear(), now.getMonth(), 1));
  }

  private static lastDayOfCurrentMonth(): string {
    const now = new Date();
    return AtypicalPricesReportComponent.toInputDate(new Date(now.getFullYear(), now.getMonth() + 1, 0));
  }

  private static displayDate(inputDate: string): string {
    const [year, month, day] = inputDate.split('-');
    return `${day}/${month}/${year}`;
  }

  private columnLetter(columnNumber: number): string {
    let result = '';
    let current = columnNumber;

    while (current > 0) {
      current--;
      result = String.fromCharCode(65 + current % 26) + result;
      current = Math.floor(current / 26);
    }

    return result;
  }
}
